import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/admin/ui/card';
import { Badge } from '@/components/admin/ui/badge';
import { Button } from '@/components/admin/ui/button';
import { Input } from '@/components/admin/ui/input';
import { ArrowLeft, Loader2, MapPin, Phone, Mail, ExternalLink, Check, Plus, Send, Paperclip, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { FEED_KINDS } from '@/lib/coordination/queues';
import { UPLOAD_LINK_FIELDS, normalizeUploadLink } from '@/lib/coordination/dayPlan';
import { MANUAL_STATUSES, LEAD_SOURCES, LEAD_OUTCOMES } from '@/lib/coordination/actionCenter';
import { TEMPLATES, renderTemplate, defaultTemplateFor, RECIPIENT_TOKEN } from '@/lib/coordination/outreachTemplates';
import { ATTACHMENT_ACCEPT, validateAttachment } from '@/lib/messages/attachments.js';
import { consoleLink } from '@/lib/staging/consoleLink';

/**
 * Coordination workbench, part 2 (17 Sep 2026): one booking's day plan. Everything shown is read
 * from what already exists (booking, the client's logistics answers, mileage calculation, talent
 * profiles, booking_notes, coordinator_changes). Two writes: upload links (same three columns the
 * Bookings tab saves) and notes (same booking_notes table the client page uses). Admin-only for now.
 */

async function authedFetch(path: string, init: RequestInit = {}) {
  const { data: { session } } = await supabaseAdmin.auth.getSession();
  if (!session) throw new Error('Not signed in');
  const res = await fetch(path, { ...init, headers: { ...(init.headers || {}), Authorization: `Bearer ${session.access_token}` } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

const longDate = (d?: string | null) => (d ? new Date(d + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Denver' }) : '-');
const when = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Denver' });
const dollars = (cents: number | null | undefined) => '$' + (Number(cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const daysLabel = (n: number | null) => (n === null ? '' : n === 0 ? 'today' : n === 1 ? 'tomorrow' : n < 0 ? `${-n} days ago` : `in ${n} days`);
const urgency = (n: number | null) => (n === null ? '' : n <= 7 ? 'text-destructive font-semibold' : n <= 30 ? 'text-amber-700 font-medium' : 'text-muted-foreground');
const kindLabel = (k: string) => FEED_KINDS.find((x: { key: string }) => x.key === k)?.label || k.replace(/_/g, ' ');
const mapsHref = (loc: { address?: string | null; lat?: number | null; lng?: number | null }) =>
  loc.lat !== null && loc.lat !== undefined && loc.lng !== null && loc.lng !== undefined
    ? `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.address || '')}`;

const CoordinationBooking = ({ bookingId: idProp, embedded = false }: { bookingId?: string; embedded?: boolean } = {}) => {
  const params = useParams<{ bookingId: string }>();
  const bookingId = idProp || params.bookingId;
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [links, setLinks] = useState<Record<string, string>>({ photos: '', videos: '', raws: '' });
  const [savingLinks, setSavingLinks] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [busyRole, setBusyRole] = useState<string | null>(null);
  const [leadDraft, setLeadDraft] = useState<{ role: string; lead_name: string; source: string; talent_id: string | null; note: string } | null>(null);
  const [leadSuggestions, setLeadSuggestions] = useState<any[]>([]);
  const [compose, setCompose] = useState<{ role: string; template: string; text: string; selected: string[]; extra: { id: string; name: string }[]; search: string; file: File | null } | null>(null);
  const [composeSuggestions, setComposeSuggestions] = useState<any[]>([]);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!bookingId) return;
    try {
      const d = await authedFetch(`/api/admin/coordination/bookings/${bookingId}`);
      setData(d);
      setLinks({ photos: d.upload_links.photos || '', videos: d.upload_links.videos || '', raws: d.upload_links.raws || '' });
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Could not load this booking');
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  const linksDirty = data ? UPLOAD_LINK_FIELDS.some((f: { key: string }) => (links[f.key] || '') !== (data.upload_links[f.key] || '')) : false;

  const saveLinks = async () => {
    for (const f of UPLOAD_LINK_FIELDS as { key: string; label: string }[]) {
      const r = normalizeUploadLink(links[f.key]);
      if (!r.ok) { toast.error(`${f.label}: ${r.error}`); return; }
    }
    setSavingLinks(true);
    try {
      const res = await authedFetch(`/api/admin/coordination/bookings/${bookingId}/upload-links`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(links) });
      setData((d: any) => (d ? { ...d, upload_links: { ...d.upload_links, ...res.upload_links } } : d));
      setLinks({ photos: res.upload_links.photos || '', videos: res.upload_links.videos || '', raws: res.upload_links.raws || '' });
      toast.success('Upload links saved');
    } catch (e: any) { toast.error(e.message || 'Could not save links'); } finally { setSavingLinks(false); }
  };

  const addNote = async () => {
    const note = noteText.trim();
    if (!note) return;
    setSavingNote(true);
    try {
      const res = await authedFetch(`/api/admin/coordination/bookings/${bookingId}/notes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }) });
      setData((d: any) => (d ? { ...d, notes: [res.note, ...(d.notes || [])] } : d));
      setNoteText('');
      toast.success('Note added');
    } catch (e: any) { toast.error(e.message || 'Could not add note'); } finally { setSavingNote(false); }
  };

  const patchRole = (role: string, changes: Record<string, unknown>) =>
    setData((d: any) => (d ? { ...d, talent: d.talent.map((t: any) => (t.role === role ? { ...t, ...changes } : t)) } : d));

  const saveOutreach = async (role: string, body: Record<string, unknown>) => {
    setBusyRole(role);
    try {
      const res = await authedFetch(`/api/admin/coordination/bookings/${bookingId}/outreach/${role}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      await load();
      return res;
    } catch (e: any) { toast.error(e.message || 'Could not save'); } finally { setBusyRole(null); }
  };

  const startLead = (role: string) => { setLeadDraft({ role, lead_name: '', source: 'site', talent_id: null, note: '' }); setLeadSuggestions([]); };

  useEffect(() => {
    const q = leadDraft?.lead_name?.trim() || '';
    if (!leadDraft || leadDraft.talent_id || q.length < 2) { setLeadSuggestions([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await authedFetch(`/api/admin/coordination/talent-search?q=${encodeURIComponent(q)}`);
        if (!cancelled) setLeadSuggestions(r.results || []);
      } catch { if (!cancelled) setLeadSuggestions([]); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [leadDraft?.lead_name, leadDraft?.talent_id, leadDraft]);

  const templateText = (role: string, key: string) => {
    if (!data) return '';
    return renderTemplate(key, { recipientName: RECIPIENT_TOKEN, hours: data.booking.hours, city: data.booking.city, eventDate: data.booking.event_date, role, senderName: data.sender?.name, senderTitle: data.sender?.title });
  };

  const startCompose = (role: string) => {
    const t = data?.talent.find((x: any) => x.role === role);
    const linked = (t?.leads || []).filter((l: any) => l.talent_id && !l.outcome).map((l: any) => l.talent_id);
    const key = defaultTemplateFor(role);
    setCompose({ role, template: key, text: templateText(role, key), selected: linked, extra: [], search: '', file: null });
    setComposeSuggestions([]);
  };

  useEffect(() => {
    const q = compose?.search?.trim() || '';
    if (!compose || q.length < 2) { setComposeSuggestions([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await authedFetch(`/api/admin/coordination/talent-search?q=${encodeURIComponent(q)}`);
        if (!cancelled) setComposeSuggestions(r.results || []);
      } catch { if (!cancelled) setComposeSuggestions([]); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [compose?.search, compose]);

  const sendCompose = async () => {
    if (!compose || sending) return;
    const recipients = [...new Set([...compose.selected, ...compose.extra.map((e) => e.id)])];
    if (!recipients.length) { toast.error('Pick at least one recipient'); return; }
    if (!compose.text.trim()) { toast.error('Message is empty'); return; }
    if (compose.file) { const c = validateAttachment(compose.file); if (!c.ok) { toast.error(c.error || 'File not allowed'); return; } }
    setSending(true);
    try {
      const res = await authedFetch(`/api/admin/coordination/bookings/${bookingId}/outreach/send`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ role: compose.role, template_key: compose.template, message_text: compose.text, recipients }) });
      let attachFails = 0;
      if (compose.file && res.sent?.length) {
        const { data: { session } } = await supabaseAdmin.auth.getSession();
        for (const m of res.sent) {
          const form = new FormData();
          form.append('file', compose.file);
          const r = await fetch(`/api/admin-messages/${m.message_id}/attachment`, { method: 'POST', headers: { Authorization: `Bearer ${session?.access_token || ''}` }, body: form });
          if (!r.ok) attachFails += 1;
        }
      }
      const failed = (res.failed || []).length;
      toast[failed || attachFails ? 'error' : 'success'](`Sent to ${res.sent?.length || 0}${failed ? `, ${failed} failed` : ''}${attachFails ? `, ${attachFails} attachment${attachFails === 1 ? '' : 's'} failed` : ''}`);
      setCompose(null);
      await load();
    } catch (e: any) { toast.error(e.message || 'Could not send'); } finally { setSending(false); }
  };

  const submitLead = async () => {
    if (!leadDraft || !leadDraft.lead_name.trim()) return;
    setBusyRole(leadDraft.role);
    try {
      await authedFetch(`/api/admin/coordination/bookings/${bookingId}/leads`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(leadDraft) });
      setLeadDraft(null);
      await load();
      toast.success('Lead logged');
    } catch (e: any) { toast.error(e.message || 'Could not log lead'); } finally { setBusyRole(null); }
  };

  const setLeadOutcome = async (role: string, leadId: string, outcome: string) => {
    try {
      const res = await authedFetch(`/api/admin/coordination/leads/${leadId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ outcome: outcome || null }) });
      patchRole(role, { leads: (data?.talent.find((t: any) => t.role === role)?.leads || []).map((l: any) => (l.id === leadId ? res.lead : l)) });
    } catch (e: any) { toast.error(e.message || 'Could not update lead'); }
  };

  if (error && embedded) return <p className="text-sm text-destructive">Could not load talent outreach: {error}</p>;
  if (!data && embedded) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading talent outreach…</div>;
  if (error) {
    return (
      <div className="p-8">
        <Link to={consoleLink("/lp-console/coordination")} className="inline-flex items-center gap-1 text-sm text-primary underline"><ArrowLeft className="h-4 w-4" /> Coordination</Link>
        <p className="mt-4 text-destructive">Could not load this booking: {error}</p>
      </div>
    );
  }
  if (!data) {
    return <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading day plan…</div>;
  }

  const { booking: b, logistics: l, talent, upload_links, notes, history, needs_text } = data;
  const plan = l.plan;
  const outreachOn = data.outreach_available !== false;
  const statusTone = (key: string) => key === 'na' ? 'text-muted-foreground' : ['not_started'].includes(key) ? 'border-slate-300 bg-slate-50 text-slate-700' : ['reached_out', 'listed_on_indeed'].includes(key) ? 'border-amber-300 bg-amber-50 text-amber-800' : key === 'media_due' ? 'border-purple-300 bg-purple-50 text-purple-700' : key === 'completed' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-sky-300 bg-sky-50 text-sky-800';

  const talentCard = (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Talent and outreach</CardTitle>
          {!outreachOn && <p className="text-xs text-amber-700 mt-1">Outreach tables not installed yet. Statuses show as Not Started and nothing here can be saved until the coordinator_outreach migration runs.</p>}
        </CardHeader>
        <CardContent className="text-sm">
          <div className="grid gap-3 md:grid-cols-3">
            {talent.map((t: any) => {
              const st = t.status || { key: 'na', label: 'N/A' };
              const applies = st.key !== 'na';
              const assigned = Boolean(t.talent_id && !t.placeholder);
              const busy = busyRole === t.role;
              return (
                <div key={t.role} className={`rounded-lg border p-3 ${t.needed ? 'border-red-300 bg-red-50/40' : 'border-border'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold uppercase text-muted-foreground">{t.label}{t.role === 'videographer' && b.video_addon_hours ? <span className="ml-1 normal-case text-foreground">· {b.video_addon_hours} hr video</span> : null}</div>
                    {applies ? <Badge variant="outline" className={`text-[10px] ${statusTone(st.key)}`}>{st.label}</Badge> : <span className="text-xs text-muted-foreground">N/A</span>}
                  </div>
                  {assigned ? (
                    <>
                      <div className="mt-1 font-medium"><Link to={consoleLink(`/lp-console/talent/${t.talent_id}`)} className="underline">{t.name || 'Talent'}</Link></div>
                      {t.phone && <div className="flex items-center gap-1 text-muted-foreground"><Phone className="h-3 w-3" /> <a href={`tel:${t.phone}`} className="underline">{t.phone}</a></div>}
                      {t.email && <div className="flex items-center gap-1 text-muted-foreground truncate"><Mail className="h-3 w-3" /> <a href={`mailto:${t.email}`} className="underline truncate">{t.email}</a></div>}
                      <div className="mt-1 text-xs text-muted-foreground">
                        {t.home ? `Home: ${t.home}` : 'No home city on profile'}
                        {t.miles_to_first_stop !== null ? ` · ${t.miles_to_first_stop} mi to first stop (straight line)` : t.has_home_coords ? '' : ' · no home coordinates'}
                      </div>
                      {b.days_out !== null && b.days_out >= 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <Button size="sm" variant={t.outreach?.five_day_checked_in_at ? 'default' : 'outline'} className="h-7 text-xs" disabled={busy || !outreachOn} onClick={() => saveOutreach(t.role, { five_day_checked_in: !t.outreach?.five_day_checked_in_at })}>
                            {t.outreach?.five_day_checked_in_at ? <><Check className="mr-1 h-3 w-3" /> 5-day check-in {when(t.outreach.five_day_checked_in_at)}</> : '5-day check-in done'}
                          </Button>
                          {b.days_out === 0 && (
                            <Button size="sm" variant={t.outreach?.day_of_checked_in_at ? 'default' : 'outline'} className="h-7 text-xs" disabled={busy || !outreachOn} onClick={() => saveOutreach(t.role, { day_of_checked_in: !t.outreach?.day_of_checked_in_at })}>
                              {t.outreach?.day_of_checked_in_at ? <><Check className="mr-1 h-3 w-3" /> Day-of check-in done</> : 'Day-of check-in done'}
                            </Button>
                          )}
                        </div>
                      )}
                      {b.days_out !== null && b.days_out < 0 && (
                        t.outreach?.media_received_at ? (
                          <div className="mt-2 rounded border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
                            Media marked received by {t.outreach.media_received_by_name || 'staff'} {when(t.outreach.media_received_at)}{t.outreach.media_received_note ? ` · ${t.outreach.media_received_note}` : ''}
                            <button type="button" className="ml-2 underline" disabled={busy} onClick={() => { if (window.confirm('Undo "media received"? The role goes back to OVERDUE Media/Form unless a form or upload is on file.')) saveOutreach(t.role, { media_received: false }); }}>Undo</button>
                          </div>
                        ) : st.key === 'media_due' ? (
                          <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" disabled={busy || !outreachOn} onClick={() => {
                            const reason = window.prompt(`Mark ${t.label} media as received?\n\nIt leaves OVERDUE Media/Form and is logged with your name. Why? (for example: talent ghosted, used the old JCF)`);
                            if (reason === null) return;
                            if (!reason.trim()) { toast.error('Add a reason to mark it received'); return; }
                            saveOutreach(t.role, { media_received: true, media_note: reason.trim() });
                          }}>Mark media received</Button>
                        ) : null
                      )}
                    </>
                  ) : applies ? (
                    <>
                      <div className="mt-1 text-red-700 font-medium">{t.needed ? 'Open — needs assigning' : t.placeholder ? 'Placeholder holds the slot' : 'Open'}</div>
                      <label className="mt-2 block text-xs text-muted-foreground">Status
                        <select value={t.outreach?.status || 'not_started'} disabled={busy || !outreachOn} onChange={(e) => saveOutreach(t.role, { status: e.target.value })} className="mt-1 block w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-xs text-foreground">
                          {(data.manual_statuses || MANUAL_STATUSES).map((m: any) => <option key={m.key} value={m.key}>{m.label}</option>)}
                        </select>
                      </label>
                    </>
                  ) : (
                    <div className="mt-1 text-muted-foreground">Not part of this package</div>
                  )}

                  {applies && (
                    <div className="mt-3 border-t pt-2">
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-xs font-semibold uppercase text-muted-foreground">Leads {t.leads?.length ? `(${t.leads.length})` : ''}</span>
                        <div className="flex items-center">
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" disabled={!outreachOn || busy} onClick={() => startCompose(t.role)}><Send className="mr-0.5 h-3 w-3" /> Message</Button>
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" disabled={!outreachOn || busy} onClick={() => startLead(t.role)}><Plus className="mr-0.5 h-3 w-3" /> Log outreach</Button>
                        </div>
                      </div>
                      {compose?.role === t.role && (
                        <div className="mt-2 space-y-2 rounded border border-primary/30 bg-rose-50/40 p-2">
                          <div className="text-xs font-semibold">Message talent about this {t.label.toLowerCase()} slot</div>
                          <div className="space-y-1">
                            <div className="text-[11px] text-muted-foreground">Recipients (our talent only; Indeed leads have no account here)</div>
                            {(t.leads || []).filter((l: any) => l.talent_id).map((l: any) => (
                              <label key={l.id} className="flex items-center gap-1.5 text-xs">
                                <input type="checkbox" checked={compose.selected.includes(l.talent_id)} onChange={(e) => setCompose({ ...compose, selected: e.target.checked ? [...compose.selected, l.talent_id] : compose.selected.filter((x) => x !== l.talent_id) })} />
                                <span className="truncate">{l.lead_name}</span>
                                {l.outcome && <span className="text-muted-foreground">· {(data.lead_outcomes || LEAD_OUTCOMES).find((o: any) => o.key === l.outcome)?.label || l.outcome}</span>}
                              </label>
                            ))}
                            {compose.extra.map((x) => (
                              <div key={x.id} className="flex items-center gap-1.5 text-xs"><Check className="h-3 w-3 text-emerald-600" /><span className="truncate">{x.name}</span><button type="button" className="text-muted-foreground" aria-label="Remove recipient" onClick={() => setCompose({ ...compose, extra: compose.extra.filter((e) => e.id !== x.id) })}><X className="h-3 w-3" /></button></div>
                            ))}
                            <div className="relative">
                              <Input value={compose.search} onChange={(e) => setCompose({ ...compose, search: e.target.value })} placeholder="Add talent by name or email…" className="h-8 text-xs" />
                              {composeSuggestions.length > 0 && (
                                <ul className="absolute z-10 mt-1 max-h-40 w-full overflow-auto rounded border bg-white text-xs shadow">
                                  {composeSuggestions.map((sug: any) => (
                                    <li key={sug.id}><button type="button" className="w-full px-2 py-1 text-left hover:bg-accent" onClick={() => { if (!compose.extra.some((e) => e.id === sug.id) && !compose.selected.includes(sug.id)) setCompose({ ...compose, extra: [...compose.extra, { id: sug.id, name: sug.name }], search: '' }); else setCompose({ ...compose, search: '' }); setComposeSuggestions([]); }}>{sug.name} <span className="text-muted-foreground">· {sug.role}{sug.city ? ` · ${sug.city}` : ''}</span></button></li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                          <label className="block text-[11px] text-muted-foreground">Template
                            <select value={compose.template} onChange={(e) => setCompose({ ...compose, template: e.target.value, text: templateText(t.role, e.target.value) })} className="mt-0.5 block w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-xs text-foreground">
                              {(data.templates || TEMPLATES).filter((tp: any) => tp.roles.includes(t.role)).map((tp: any) => <option key={tp.key} value={tp.key}>{tp.label}</option>)}
                            </select>
                          </label>
                          <textarea value={compose.text} onChange={(e) => setCompose({ ...compose, text: e.target.value })} rows={9} maxLength={4000} className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs" />
                          <div className="text-[11px] text-muted-foreground">{RECIPIENT_TOKEN} becomes each recipient&apos;s first name.</div>
                          <div className="flex items-center justify-between gap-2">
                            <label className="flex items-center gap-1 text-[11px] text-muted-foreground cursor-pointer">
                              <Paperclip className="h-3 w-3" />
                              <span className="truncate max-w-[160px]">{compose.file ? compose.file.name : 'Attach (JPEG, PNG, PDF)'}</span>
                              <input type="file" accept={ATTACHMENT_ACCEPT} className="hidden" onChange={(e) => setCompose({ ...compose, file: e.target.files?.[0] || null })} />
                              {compose.file && <button type="button" aria-label="Remove attachment" onClick={(e) => { e.preventDefault(); setCompose({ ...compose, file: null }); }}><X className="h-3 w-3" /></button>}
                            </label>
                            <div className="flex gap-1.5">
                              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setCompose(null)}>Cancel</Button>
                              <Button size="sm" className="h-7 text-xs" disabled={sending || (!compose.selected.length && !compose.extra.length)} onClick={sendCompose}>{sending ? <Loader2 className="h-3 w-3 animate-spin" /> : `Send to ${new Set([...compose.selected, ...compose.extra.map((e) => e.id)]).size}`}</Button>
                            </div>
                          </div>
                        </div>
                      )}
                      {leadDraft?.role === t.role && (
                        <div className="mt-2 space-y-1.5 rounded border border-slate-200 bg-slate-50 p-2">
                          <div className="relative">
                            <Input value={leadDraft.lead_name} onChange={(e) => setLeadDraft({ ...leadDraft, lead_name: e.target.value, talent_id: null })} placeholder="Who did you contact?" className="h-8 text-xs" autoFocus />
                            {leadSuggestions.length > 0 && (
                              <ul className="absolute z-10 mt-1 max-h-40 w-full overflow-auto rounded border bg-white text-xs shadow">
                                {leadSuggestions.map((sug: any) => (
                                  <li key={sug.id}><button type="button" className="w-full px-2 py-1 text-left hover:bg-accent" onClick={() => { setLeadDraft({ ...leadDraft, lead_name: sug.name, talent_id: sug.id }); setLeadSuggestions([]); }}>{sug.name} <span className="text-muted-foreground">· {sug.role}{sug.city ? ` · ${sug.city}` : ''}</span></button></li>
                                ))}
                              </ul>
                            )}
                          </div>
                          {leadDraft.talent_id && <div className="text-[11px] text-emerald-700">Linked to their talent profile</div>}
                          <div className="flex gap-1.5">
                            <select value={leadDraft.source} onChange={(e) => setLeadDraft({ ...leadDraft, source: e.target.value })} className="rounded border border-slate-300 bg-white px-1.5 py-1 text-xs">
                              {(data.lead_sources || LEAD_SOURCES).map((o: any) => <option key={o.key} value={o.key}>{o.label}</option>)}
                            </select>
                            <Input value={leadDraft.note} onChange={(e) => setLeadDraft({ ...leadDraft, note: e.target.value })} placeholder="note (optional)" className="h-8 text-xs" />
                          </div>
                          <div className="flex justify-end gap-1.5">
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setLeadDraft(null)}>Cancel</Button>
                            <Button size="sm" className="h-7 text-xs" disabled={!leadDraft.lead_name.trim() || busy} onClick={submitLead}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Log'}</Button>
                          </div>
                        </div>
                      )}
                      {(t.leads || []).length > 0 && (
                        <ul className="mt-2 space-y-1.5">
                          {t.leads.map((ld: any) => (
                            <li key={ld.id} className="text-xs">
                              <div className="flex items-center justify-between gap-2">
                                <span className="min-w-0 truncate">
                                  {ld.talent_id ? <Link to={consoleLink(`/lp-console/talent/${ld.talent_id}`)} className="underline">{ld.lead_name}</Link> : ld.lead_name}
                                  <span className="text-muted-foreground"> · {(data.lead_sources || LEAD_SOURCES).find((o: any) => o.key === ld.source)?.label || ld.source} · {when(ld.contacted_at)}</span>
                                </span>
                                <select value={ld.outcome || ''} onChange={(e) => setLeadOutcome(t.role, ld.id, e.target.value)} className="shrink-0 rounded border border-slate-300 bg-white px-1 py-0.5 text-[11px]" aria-label="Outcome">
                                  <option value="">awaiting reply</option>
                                  {(data.lead_outcomes || LEAD_OUTCOMES).map((o: any) => <option key={o.key} value={o.key}>{o.label}</option>)}
                                </select>
                              </div>
                              {ld.note && <div className="text-muted-foreground">{ld.note}</div>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Assigning still happens on the Bookings tab. Messaging or logging outreach here moves an untouched slot to Reached Out (or Listed on Indeed) by itself; messages land in the talent&apos;s Messages page and the hourly unread email.</p>
        </CardContent>
      </Card>
  );

  const linksCard = (
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-lg">Box upload links</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          {(UPLOAD_LINK_FIELDS as { key: string; label: string }[]).map((f) => (
            <label key={f.key} className="block">
              <span className="text-xs font-semibold uppercase text-muted-foreground">{f.label}</span>
              <div className="mt-1 flex items-center gap-2">
                <Input value={links[f.key] || ''} onChange={(e) => setLinks((s) => ({ ...s, [f.key]: e.target.value }))} placeholder="https://…" className="h-9 text-sm" />
                {upload_links[f.key] && <a href={upload_links[f.key]} target="_blank" rel="noreferrer" className="text-primary" aria-label={`Open ${f.label} link`}><ExternalLink className="h-4 w-4" /></a>}
              </div>
            </label>
          ))}
          {upload_links.media_upload_url && <p className="text-xs text-muted-foreground break-all">Talent upload: <a href={upload_links.media_upload_url} target="_blank" rel="noreferrer" className="underline">{upload_links.media_upload_url}</a></p>}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Same links the Bookings tab shows to talent.</span>
            <Button size="sm" disabled={!linksDirty || savingLinks} onClick={saveLinks}>{savingLinks ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save links'}</Button>
          </div>
        </CardContent>
      </Card>
  );

  // Nautica, 4 Oct 2026: the Full Booking page shows these two cards for Admin and Talent.
  if (embedded) return <div className="space-y-5">{talentCard}{linksCard}</div>;

  return (
    <div className="p-8">
      <Link to={consoleLink("/lp-console/coordination")} className="inline-flex items-center gap-1 text-sm text-primary underline"><ArrowLeft className="h-4 w-4" /> Coordination</Link>

      <div className="mt-3 mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {b.customer_id ? <Link to={consoleLink(`/lp-console/clients/${b.customer_id}`)} className="hover:underline">{b.customer_name || 'Client'}</Link> : (b.customer_name || 'Client')}
            {b.is_test && <Badge variant="outline" className="ml-2 align-middle text-xs">test booking</Badge>}
          </h1>
          <p className="mt-1 text-lg">{longDate(b.event_date)} <span className={`text-sm ${urgency(b.days_out)}`}>· {daysLabel(b.days_out)}</span></p>
          <p className="text-sm text-muted-foreground">
            {[b.package, b.hours ? `${b.hours} hr` : null, b.city, b.venue].filter(Boolean).join(' · ')}
            {b.add_ons?.length ? <span className="block">Add-ons: {b.add_ons.join(', ')}</span> : null}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
            <Badge variant="outline" className="capitalize">{String(b.booking_status || '').replace(/_/g, ' ')}</Badge>
            {needs_text && <Badge variant="outline" className="border-red-300 bg-red-50 text-red-700">Needs {needs_text}</Badge>}
            {talent.some((t: any) => t.talent_id && !t.placeholder) && (b.talent_confirmed_seen
              ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">talent confirmed{b.talent_confirmed_seen_at ? ` ${when(b.talent_confirmed_seen_at)}` : ''}</Badge>
              : <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">not confirmed by talent</Badge>)}
            <Badge variant="outline" className="capitalize">logistics {String(l.status).replace(/_/g, ' ')}{l.updated_at ? ` · ${when(l.updated_at)}` : ''}</Badge>
          </div>
        </div>
        <div className="text-sm text-right text-muted-foreground">
          {b.customer_phone && <div className="flex items-center justify-end gap-1"><Phone className="h-3.5 w-3.5" /> <a href={`tel:${b.customer_phone}`} className="underline">{b.customer_phone}</a></div>}
          {b.customer_email && <div className="flex items-center justify-end gap-1"><Mail className="h-3.5 w-3.5" /> <a href={`mailto:${b.customer_email}`} className="underline">{b.customer_email}</a></div>}
          {(l.day_of_contact.name || l.day_of_contact.phone) && <div className="mt-1">Day-of contact: {l.day_of_contact.name}{l.day_of_contact.phone ? <> · <a href={`tel:${l.day_of_contact.phone}`} className="underline">{l.day_of_contact.phone}</a></> : null}</div>}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-lg">Day plan</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-5">
              {l.timeline.length === 0 ? <p className="text-muted-foreground">No times on file yet. The client logistics form is {String(l.status).replace(/_/g, ' ')}.</p> : (
                <ol className="space-y-2">
                  {l.timeline.map((e: any) => (
                    <li key={e.key} className="flex gap-3">
                      <span className="w-20 shrink-0 tabular-nums font-medium">{e.display || <span className="text-muted-foreground">—</span>}</span>
                      <span>
                        <span className="font-medium">{e.label}</span>
                        {e.place && <span className="text-muted-foreground"> · {e.place}</span>}
                        {e.note && <span className="block text-xs text-muted-foreground">{e.note}</span>}
                      </span>
                    </li>
                  ))}
                </ol>
              )}

              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <h3 className="font-semibold">Stops and legs</h3>
                  <span className="text-xs text-muted-foreground">
                    {plan.totalMiles !== null ? `${plan.totalMiles} mi total${plan.routed ? ' (routed)' : ' (straight-line estimate)'}` : plan.legs.length ? 'distance unknown' : ''}
                    {Number(b.mileage_fee_cents) > 0 ? ` · mileage fee ${dollars(b.mileage_fee_cents)}` : ''}
                  </span>
                </div>
                {plan.locations.length === 0 ? <p className="text-muted-foreground">No locations on the logistics form{b.venue ? `; booking venue: ${b.venue}` : ''}.</p> : (
                  <ol className="space-y-1.5">
                    {plan.locations.map((loc: any, i: number) => (
                      <li key={loc.order}>
                        {i > 0 && plan.legs[i - 1] && (
                          <div className="ml-2 my-1 border-l-2 border-dashed border-slate-300 pl-3 text-xs text-muted-foreground">
                            {plan.legs[i - 1].miles !== null ? `${plan.legs[i - 1].miles} mi${plan.legs[i - 1].source === 'estimate' ? ' (straight line)' : ''}` : 'distance unknown'}
                          </div>
                        )}
                        <div className="flex items-start gap-2">
                          <MapPin className={`mt-0.5 h-4 w-4 shrink-0 ${loc.verified ? 'text-primary' : 'text-amber-600'}`} />
                          <div className="min-w-0">
                            <div className="font-medium">{loc.label}</div>
                            <div className="text-muted-foreground break-words">{loc.address} <a href={mapsHref(loc)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-primary underline">map <ExternalLink className="h-3 w-3" /></a></div>
                            {!loc.verified && <div className="text-xs text-amber-700">typed address, not verified on a map</div>}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
                {plan.refused && <p className="mt-2 text-xs text-amber-700">Mileage calculation refused: {plan.refused.reason.replace(/_/g, ' ')}{plan.refused.detail ? ` — ${plan.refused.detail}` : ''}</p>}
                {(Number(b.travel_fee) > 0 || b.travel_distance_miles) && <p className="mt-2 text-xs text-muted-foreground">Travel: {Number(b.travel_fee) > 0 ? dollars(Math.round(Number(b.travel_fee) * 100)) : 'no fee'}{b.travel_distance_miles ? ` · ${Math.round(b.travel_distance_miles)} mi from base` : ''}</p>}
              </div>

              {(l.must_have_photos || l.venue_restrictions || l.parking_info || l.special_requests || b.special_requests || b.guests) && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {b.guests && <div><div className="text-xs font-semibold uppercase text-muted-foreground">Guests</div><div>{b.guests}</div></div>}
                  {l.must_have_photos && <div><div className="text-xs font-semibold uppercase text-muted-foreground">Must-have photos</div><div className="whitespace-pre-wrap">{l.must_have_photos}</div></div>}
                  {l.venue_restrictions && <div><div className="text-xs font-semibold uppercase text-muted-foreground">Venue restrictions</div><div className="whitespace-pre-wrap">{l.venue_restrictions}</div></div>}
                  {l.parking_info && <div><div className="text-xs font-semibold uppercase text-muted-foreground">Parking</div><div className="whitespace-pre-wrap">{l.parking_info}</div></div>}
                  {l.special_requests && <div><div className="text-xs font-semibold uppercase text-muted-foreground">Special requests (logistics)</div><div className="whitespace-pre-wrap">{l.special_requests}</div></div>}
                  {b.special_requests && <div><div className="text-xs font-semibold uppercase text-muted-foreground">Special requests (booking)</div><div className="whitespace-pre-wrap">{b.special_requests}</div></div>}
                </div>
              )}
            </CardContent>
          </Card>

          {talentCard}
        </div>

        <div className="space-y-6">
          {linksCard}

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-lg">Notes</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} maxLength={4000} placeholder="Add a coordination note (visible to staff on the client page too)…" className="w-full rounded-md border border-slate-300 bg-background px-3 py-2 text-sm" />
              <div className="flex justify-end"><Button size="sm" disabled={!noteText.trim() || savingNote} onClick={addNote}>{savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add note'}</Button></div>
              {notes.length === 0 ? <p className="text-muted-foreground">No notes yet.</p> : (
                <ul className="divide-y">
                  {notes.map((n: any) => (
                    <li key={n.id} className="py-2">
                      <div className="whitespace-pre-wrap break-words">{n.note}</div>
                      <div className="text-xs text-muted-foreground">{when(n.created_at)} · {n.created_by_name}</div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-lg">History</CardTitle></CardHeader>
            <CardContent className="text-sm">
              {history.length === 0 ? <p className="text-muted-foreground">No recorded changes for this booking.</p> : (
                <ol className="divide-y">
                  {history.map((h: any) => (
                    <li key={h.change_key} className="py-2">
                      <div className="flex flex-wrap items-center gap-1.5"><Badge variant="outline" className="text-[10px]">{kindLabel(h.kind)}</Badge><span className="text-xs text-muted-foreground">{when(h.at)}{h.actor_name ? ` · by ${h.actor_name}` : ''}</span></div>
                      <div className="text-muted-foreground break-words">{h.summary}</div>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CoordinationBooking;
